import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LayoutDashboard, Settings } from "lucide-mui";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { keys } from "../../api/queryKeys.ts";
import { api, getErrorMessage } from "../../api/client.ts";
import { userCanManageOrgApps } from "../../auth/scopes";
import ScopeChip from "../../components/ScopeChip.tsx";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import PageHeader from "../../components/PageHeader.tsx";

function MicroAppsIndexPage() {
  const { user } = useAuth();
  const { selectedOrgId, currentOrg } = useOrganization();
  const { tenants } = useTenant();
  const canManageApps = userCanManageOrgApps(user, selectedOrgId);

  // Show every app across the current org's tenants, not just one tenant.
  const { data, error, isLoading } = useQuery({
    queryKey: keys.microApp.list(selectedOrgId),
    queryFn: () =>
      api.getMicroApps({ organization_id: selectedOrgId, enabled_only: true }),
  });

  const tenantNameById = useMemo(
    () => new Map(tenants.map((t) => [t.id, t.name])),
    [tenants],
  );

  const apps = data ?? [];

  return (
    <Box>
      <PageHeader
        title="Apps"
        description={
          <>
            Dashboards published into Vayken{currentOrg ? ` for ${currentOrg.name}` : ""}, each scoped
            to your own access
            {data ? ` · ${apps.length} ${apps.length === 1 ? "app" : "apps"}` : ""}
          </>
        }
        actions={
          canManageApps && (
            <Tooltip title="Manage apps">
              <IconButton component={RouterLink} to="/settings/micro-apps" aria-label="Manage apps">
                <Settings />
              </IconButton>
            </Tooltip>
          )
        }
      />

      {isLoading ? (
        <CenteredSpinner py={6} />
      ) : error ? (
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      ) : apps.length === 0 ? (
        <EmptyState
          variant="panel"
          icon={<LayoutDashboard />}
          title="No apps yet"
          description="Dashboards an agent publishes into Vayken show up here."
        />
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {apps.map((app) => (
            <Card
              key={app.id}
              variant="outlined"
              sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 260 }}
            >
              <CardActionArea
                component={RouterLink}
                to={`/apps/${app.slug}`}
                sx={{ height: "100%" }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1} sx={{ mb: 0.5, alignItems: "center" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {app.title}
                    </Typography>
                    <ScopeChip
                      tenantId={app.tenant_id}
                      tenantName={
                        app.tenant_id
                          ? (tenantNameById.get(app.tenant_id) ?? "Unknown tenant")
                          : undefined
                      }
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {app.description || "No description."}
                  </Typography>
                  {app.current_version_id === null ? (
                    <Chip size="small" color="warning" label="Not published" sx={{ mt: 1 }} />
                  ) : null}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default MicroAppsIndexPage;
